// Hand-maintained editorial content that release artifacts do not carry.
// Release files (data/photon/releases/*.json) are machine-produced; anything
// that is voice/prose rather than data lives here.

// One-line description per model family, shown on the /photon family cards.
// New families fall back to a generic line until someone writes one.
export const FAMILY_DESCRIPTIONS: Record<string, string> = {
  Moondream:
    "Open vision-language models for local perception and physical AI.",
  "Qwen 3":
    "Open multimodal models in four sizes, compiled through the same path.",
  "Gemma":
    "Google open multimodal models in two sizes, verified on H100.",
};

export const familyDescription = (family: string) =>
  FAMILY_DESCRIPTIONS[family] ??
  `${family} models, compiled through the same path.`;

// Full working example per model family, keyed by family name. Sample code
// is editorial, not release data — the API surface follows
// docs.moondream.ai/running-locally (md.photon, caption, query, detect, chat).
export type FamilySnippet = {
  /** The model identifier passed to md.photon(). */
  modelId: string;
  /** Full script for image + text use. */
  imageText: string;
  /** Full script for text-only use. */
  text: string;
};

export const FAMILY_SNIPPETS: Record<string, FamilySnippet> = {
  Moondream: {
    modelId: "moondream3-preview",
    imageText: `# pip install moondream
import moondream as md
from PIL import Image

# Moondream 3 on Photon — local GPU inference
model = md.photon("moondream3-preview")

image = Image.open("photo.jpg")

# Caption the image
caption = model.caption(image)["caption"]
print("Caption:", caption)

# Ask a question
answer = model.query(image, "What's happening in this image?")["answer"]
print("Answer:", answer)

# Moondream 3 also detects, points, and segments
objects = model.detect(image, "person")["objects"]
for obj in objects:
    print("Person:", obj)
`,
    text: `# pip install moondream
import moondream as md

model = md.photon("moondream3-preview")

# Text-only chat
chat = model.chat([
    {"role": "user", "content": "Describe a warehouse shelf in one sentence."},
])

print(chat["message"]["content"])
`,
  },
  "Qwen 3": {
    modelId: "Qwen/Qwen3.5-4B",
    imageText: `# pip install moondream
import moondream as md
from PIL import Image

# Any Qwen 3 size works: Qwen/Qwen3.5-0.8B through Qwen/Qwen3.5-9B,
# and Qwen/Qwen3.6-0.8B
model = md.photon("Qwen/Qwen3.5-4B")

image = Image.open("photo.jpg")

caption = model.caption(image)["caption"]
print("Caption:", caption)

answer = model.query(image, "What's in this image?")["answer"]
print("Answer:", answer)
`,
    text: `# pip install moondream
import moondream as md

model = md.photon("Qwen/Qwen3.5-4B")

# Text-only chat
chat = model.chat([
    {"role": "user", "content": "Write a one-line alt text for a product photo."},
])

print(chat["message"]["content"])
`,
  },
  Gemma: {
    modelId: "google/gemma-4-E2B-it",
    imageText: `# pip install moondream
import moondream as md
from PIL import Image

# Or google/gemma-4-E4B-it for the larger variant
model = md.photon("google/gemma-4-E2B-it")

image = Image.open("photo.jpg")

caption = model.caption(image)["caption"]
print("Caption:", caption)

answer = model.query(image, "What's in this image?")["answer"]
print("Answer:", answer)
`,
    text: `# pip install moondream
import moondream as md

model = md.photon("google/gemma-4-E2B-it")

# Text-only chat
chat = model.chat([
    {"role": "user", "content": "Write a one-line alt text for a product photo."},
])

print(chat["message"]["content"])
`,
  },
};

export const familySnippet = (family: string) => FAMILY_SNIPPETS[family];
